import { supabase } from '../lib/supabase';

export const hrDocumentApprovalService = {
  // ============================================================================
  // GET PENDING DOCUMENTS FOR APPROVAL
  // ============================================================================

  async getPendingDocuments(filters = {}) {
    try {
      let query = supabase
        ?.from('employee_documents')
        ?.select(`
          *,
          employees (
            employee_number,
            first_name,
            last_name,
            department,
            position,
            email
          )
        `)
        ?.eq('approval_status', 'pending_approval')
        ?.order('submitted_for_approval_at', { ascending: true });

      // Filter by category
      if (filters?.category) {
        query = query?.eq('document_category', filters?.category);
      }

      // Filter by department
      if (filters?.department) {
        query = query?.eq('employees.department', filters?.department);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(doc => ({
        id: doc?.id,
        employeeId: doc?.employee_id,
        employeeName: `${doc?.employees?.first_name} ${doc?.employees?.last_name}`,
        employeeNumber: doc?.employees?.employee_number,
        department: doc?.employees?.department,
        position: doc?.employees?.position,
        category: doc?.document_category,
        documentName: doc?.document_name,
        fileName: doc?.file_name,
        fileSizeBytes: doc?.file_size_bytes,
        storagePath: doc?.storage_path,
        mimeType: doc?.mime_type,
        documentDate: doc?.document_date,
        description: doc?.description,
        tags: doc?.tags || [],
        uploadedBy: doc?.uploaded_by,
        uploadedAt: doc?.uploaded_at,
        submittedForApprovalAt: doc?.submitted_for_approval_at,
        approvalStatus: doc?.approval_status
      })) || [];
    } catch (error) {
      console.error('Error fetching pending documents:', error);
      throw error;
    }
  },

  // ============================================================================
  // GET DOCUMENT DETAILS
  // ============================================================================

  async getDocumentDetails(documentId) {
    try {
      const { data, error } = await supabase
        ?.from('employee_documents')
        ?.select(`
          *,
          employees (
            employee_number,
            first_name,
            last_name,
            department,
            position,
            email,
            phone
          )
        `)
        ?.eq('id', documentId)
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        employeeId: data?.employee_id,
        employeeName: `${data?.employees?.first_name} ${data?.employees?.last_name}`,
        employeeNumber: data?.employees?.employee_number,
        employeeEmail: data?.employees?.email,
        employeePhone: data?.employees?.phone,
        department: data?.employees?.department,
        position: data?.employees?.position,
        category: data?.document_category,
        documentName: data?.document_name,
        fileName: data?.file_name,
        fileSizeBytes: data?.file_size_bytes,
        storagePath: data?.storage_path,
        mimeType: data?.mime_type,
        documentDate: data?.document_date,
        description: data?.description,
        tags: data?.tags || [],
        uploadedBy: data?.uploaded_by,
        uploadedAt: data?.uploaded_at,
        submittedForApprovalAt: data?.submitted_for_approval_at,
        approvalStatus: data?.approval_status,
        reviewedBy: data?.reviewed_by,
        reviewedAt: data?.reviewed_at,
        approvalComments: data?.approval_comments,
        rejectionReason: data?.rejection_reason,
        hrSignatureName: data?.hr_signature_name,
        hrSignatureTitle: data?.hr_signature_title,
        hrSignatureDate: data?.hr_signature_date
      };
    } catch (error) {
      console.error('Error fetching document details:', error);
      throw error;
    }
  },

  // ============================================================================
  // APPROVE DOCUMENT
  // ============================================================================

  async approveDocument(documentId, reviewedBy, comments, signatureName, signatureTitle) {
    try {
      const { error } = await supabase
        ?.rpc('approve_document', {
          p_document_id: documentId,
          p_reviewed_by: reviewedBy,
          p_comments: comments,
          p_signature_name: signatureName,
          p_signature_title: signatureTitle
        });

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error approving document:', error);
      throw error;
    }
  },

  // ============================================================================
  // REJECT DOCUMENT
  // ============================================================================

  async rejectDocument(documentId, reviewedBy, rejectionReason) {
    try {
      const { error } = await supabase
        ?.rpc('reject_document', {
          p_document_id: documentId,
          p_reviewed_by: reviewedBy,
          p_rejection_reason: rejectionReason
        });

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error rejecting document:', error);
      throw error;
    }
  },

  // ============================================================================
  // GET DOCUMENT URL
  // ============================================================================

  async getDocumentUrl(storagePath) {
    try {
      const { data, error } = await supabase
        ?.storage
        ?.from('employee-documents')
        ?.createSignedUrl(storagePath, 3600); // 1 hour expiry

      if (error) throw error;

      return data?.signedUrl;
    } catch (error) {
      console.error('Error getting document URL:', error);
      throw error;
    }
  },

  // ============================================================================
  // GET APPROVAL STATISTICS
  // ============================================================================

  async getApprovalStatistics() {
    try {
      const { data, error } = await supabase
        ?.from('employee_documents')
        ?.select('approval_status');

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        pending: data?.filter(d => d?.approval_status === 'pending_approval')?.length || 0,
        approved: data?.filter(d => d?.approval_status === 'approved')?.length || 0,
        rejected: data?.filter(d => d?.approval_status === 'rejected')?.length || 0
      };

      return stats;
    } catch (error) {
      console.error('Error fetching approval statistics:', error);
      throw error;
    }
  }
};

export default hrDocumentApprovalService;